import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ChevronRight, Home } from "lucide-react";
import { getNeighborhood } from "@/data/neighborhoods";
import { getArticle } from "@/data/articles";

const SITE_URL = "https://www.kinshipcarecollective.com";

const pathLabels: Record<string, string> = {
  "/services": "Services",
  "/for-families": "For Families",
  "/for-seniors": "For Seniors",
  "/for-caregivers": "For Caregivers",
  "/about": "About",
  "/resources": "Resources",
  "/contact": "Contact",
  "/care-request": "Request Care",
  "/senior-companion-care-nashville": "Senior Companion Care in Nashville",
  "/nashville-senior-care": "Nashville Senior Care",
  "/shop/caregiver-kit": "The Caregiver Companion Kit",
};

const Breadcrumbs = () => {
  const location = useLocation();

  if (location.pathname === "/") return null;

  let label = pathLabels[location.pathname];
  let parent: { path: string; label: string } | null = null;

  if (!label) {
    const areaMatch = location.pathname.match(/^\/areas\/([^/]+)$/);
    const articleMatch = location.pathname.match(/^\/resources\/([^/]+)$/);
    if (areaMatch) {
      const area = getNeighborhood(areaMatch[1]);
      if (area) {
        label = `${area.name}, TN`;
        parent = { path: "/services", label: "Services" };
      }
    } else if (articleMatch) {
      const article = getArticle(articleMatch[1]);
      if (article) {
        label = article.title;
        parent = { path: "/resources", label: "Resources" };
      }
    }
  }
  if (!label) return null;

  const trail = [
    { name: "Home", url: `${SITE_URL}/` },
    ...(parent ? [{ name: parent.label, url: `${SITE_URL}${parent.path}` }] : []),
    { name: label, url: `${SITE_URL}${location.pathname}` },
  ];

  return (
    <nav aria-label="Breadcrumb" className="bg-muted/50 border-b border-border">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: trail.map((crumb, index) => ({
              "@type": "ListItem",
              position: index + 1,
              name: crumb.name,
              item: crumb.url,
            })),
          })}
        </script>
      </Helmet>
      <div className="container py-2.5">
        <ol className="flex items-center gap-1.5 text-xs text-muted-foreground" itemScope itemType="https://schema.org/BreadcrumbList">
          <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
            <Link to="/" className="flex items-center gap-1 hover:text-foreground transition-colors" itemProp="item">
              <Home className="h-3 w-3" aria-hidden="true" />
              <span itemProp="name">Home</span>
            </Link>
            <meta itemProp="position" content="1" />
          </li>
          <li aria-hidden="true"><ChevronRight className="h-3 w-3" /></li>
          {parent && (
            <>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link to={parent.path} className="hover:text-foreground transition-colors" itemProp="item">
                  <span itemProp="name">{parent.label}</span>
                </Link>
                <meta itemProp="position" content="2" />
              </li>
              <li aria-hidden="true"><ChevronRight className="h-3 w-3" /></li>
            </>
          )}
          <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
            <span className="text-foreground font-medium truncate max-w-[60vw] inline-block align-bottom" itemProp="name">{label}</span>
            <meta itemProp="position" content={parent ? "3" : "2"} />
          </li>
        </ol>
      </div>
    </nav>
  );
};

export default Breadcrumbs;
